import { create } from "zustand";
import { ResType, Widget } from "../interfaces";
import { ContinueDraft } from "../api/continueDraft.api";
import { Payload } from "../interfaces/appAndPayload/Payload.type";
import useAppStore from "./AppStore";
import { ContactUsType } from "../interfaces/contactUs/ContactUs.type";
import {FinalizeButtonType} from "../interfaces/finalizeButton/FinalizeButton.type";
import { getCookies, myListingRedirect } from "../helpers/commonFunctions";
import { ActionDimensions } from "../membership/logging/dimensions/ActionDimensions";
import { Toast } from "../interfaces/globalTypes/GlobalTypes.type";
import toastMessages from "../utils/toastMessages.json";
import {
  SkipContinueButtonType,
} from "../interfaces/skipContinueButton/skipContinueButton.type";
import {ContinueButtonType} from "../interfaces/continueButton/ContinueButton.type";
import {PostCartInfoType} from "../interfaces/postCartInfo/PostCartInfo.type";
import { BottomSheetType } from "../interfaces/bottomSheet/BottomSheet.type";

type State = {
  components: Widget[];
  contactBox: ContactUsType | null;
  postBox: PostCartInfoType | null;
  bottomSheet:BottomSheetType | null;
  appLoading: boolean;
  isSelectingPayPerListing: boolean;
  isSelectingTab: boolean;
  isCheckingAddon: boolean;
  button: FinalizeButtonType | SkipContinueButtonType | ContinueButtonType | null;
  prevData: any;
  closeButtonText?: string;
  closeButtonTitle?: string;
  forceRenderContactUs?: boolean;
  forceRenderPostCartInfo?: boolean;
  _handleToastTrigger: (toast: Toast) => void;
};

type Actions = {
  setComponents: (data: ResType) => void;
  _clearComponents: () => void;
  setIsSelectingPayPerListing: (value: boolean) => void;
  setIsCheckingAddon: (value: boolean) => void;
  setIsSelectingTab: (value: boolean) => void;
  continueDraft: (body: Payload) => Promise<void>;
  setHandleToastTrigger: (handleToastTrigger: (toast: Toast) => void) => void;
  toastHandler: (
    toastType: "error" | "success" | "info" | "warning",
    autoClose?: number,
    description?: string
  ) => void;
};

const useComponentsStore = create<State & Actions>((set, get, state) => ({
  components: [],
  button: null,
  contactBox: null,
  postBox: null,
  bottomSheet:  null,
  appLoading: true,
  isSelectingPayPerListing: false,
  isCheckingAddon: false,
  isSelectingTab: false,
  prevData: null,
  closeButtonText: null,
  closeButtonTitle: null,
  forceRenderContactUs: false,
  forceRenderPostCartInfo: false,
  _handleToastTrigger: () => {},
  toastHandler: (toastType, autoClose, description) => {
    const lang = getCookies().NEXT_LOCALE;
    get()._handleToastTrigger({
      toastType,
      locale: lang,
      description: description || toastMessages[toastType][lang],
      autoClose: autoClose || 0,
    });
  },
  setHandleToastTrigger: (_handleToastTrigger: (toast: Toast) => void) => {
    set((state) => ({
      _handleToastTrigger,
    }));
  },
  setIsSelectingPayPerListing: (value: boolean) =>
    set({ isSelectingPayPerListing: value }),
  setIsSelectingTab: (value: boolean) =>
      set({ isSelectingTab: value}),
  setIsCheckingAddon: (value: boolean) =>
      set({ isCheckingAddon: value}),
  _clearComponents: () =>
    set({
      components: [],
      contactBox: null,
      postBox: null,
      bottomSheet:null,
      button: null,
      appLoading: true,
    }),
  setComponents: (data: ResType) => {
    get()._clearComponents();
    const finalizedWidgets = data.widgets;
    if (data.button) {
      finalizedWidgets.push(data.button);
    }
    set({
      components: finalizedWidgets,
      appLoading: false,
      contactBox: data?.box,
      postBox: data?.postBox,
      bottomSheet:data?.bottomSheet,
      button: data.button,
      prevData:
        data?.skipConfirmation?.type == "back" && data?.skipConfirmation?.undo
          ? data?.skipConfirmation?.undo
          : null,
      closeButtonText: data?.closeButton?.message || null,
      closeButtonTitle: data?.closeButton?.title || null,
      forceRenderContactUs: data?.forceRenderContactUs ?? false,
      forceRenderPostCartInfo: data?.forceRenderPostCartInfo ?? false
    });
  },
  continueDraft: async (body: Payload) => {
    try {
      const res = await ContinueDraft(body, useAppStore.getState().token);
      if (res.status < 300 && res.status >= 200) {
        const outcome = res.data.outcome;
        const completeFlow = res.data.completeFlow;
        if (completeFlow) return myListingRedirect(500);
        if (outcome === "success") {
          get().toastHandler("success", 2000);
          return myListingRedirect(2000);
        }
        if (outcome === "checkout") {
          window.location.href =
            res.data.payload.redirect +
            `&SessionFunnelSource=${
              ActionDimensions.getInstance().sessionFunnelSource
            }`;
          return;
        }
        get().setComponents(res.data);
      }
    } catch (err) {
      get().toastHandler("error", 2000);
    }
  },
}));

export default useComponentsStore;
